import { useEffect, useMemo } from 'react';
import type { Timesheet, TimesheetEntry } from '@/types/timesheet';

export interface UseTimesheetDirtyStateParams {
  entries: TimesheetEntry[];
  currentTimesheet: Timesheet;
  isReadOnly: boolean;
}

/**
 * True when local entries differ from the last-persisted context copy, plus the
 * browser tab / window close guard that warns about those unsaved changes.
 */
export function useTimesheetDirtyState({ entries, currentTimesheet, isReadOnly }: UseTimesheetDirtyStateParams): boolean {
  const isDirty = useMemo(() => {
    if (isReadOnly) return false;
    if (entries.length !== currentTimesheet.entries.length) return true;
    return JSON.stringify(entries) !== JSON.stringify(currentTimesheet.entries);
  }, [entries, currentTimesheet.entries, isReadOnly]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return isDirty;
}
